/**
 * `useCitationDeepLink` — the deep-link revisit half of permalink-citations.
 *
 * When a document view opens with `?comment=<id>`, this hook: (1) resolves the
 * citation to its durable anchor + doc handle via the backend (which walks a
 * reply up to its thread ROOT), (2) once the canvas is painted, scrolls to +
 * flash-highlights the cited span, and (3) reports the resolution so the host
 * opens the matching comment thread. Fires exactly ONCE per (doc, comment).
 *
 * Composition (RULE ZERO): the REVEAL is injected as a single `reveal` callback
 * — in production `<ReadCanvas>`'s `focusAnchor`, which scrolls to the anchor's
 * page rects and blooms them. The resolve is the shared `api` client. This hook
 * is thin glue: it re-solves no anchoring and owns no fetch transport.
 *
 * IT NO LONGER KNOWS ABOUT SPANS. It used to take a DOM `root` plus `RevealOps`
 * (`firstSpanOf` / `flashAnnotation`) and locate a `<span class="docz-comment">`.
 * The canvas has no spans and no root element to search — the anchor is
 * `(blockOrd, byte range)` and only the surface can turn that into pixels — so
 * "did it reveal" is now the surface's answer, not this hook's DOM query.
 *
 * IMPORTANT (integration seam): this hook does NOT touch the DocumentViewer
 * annotation/comment code. A host wires it in ~3 lines. It is pure React over an
 * injected `reveal`, so its orchestration is unit-tested by passing a recording
 * stand-in for the surface — no DOM, no WASM boot.
 */

import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import type { CitationResolution } from "../api/citationTypes";
import type { AnchorState } from "./anchoring";
import { parseCitationParam } from "./permalink";

/** The outcome of a reveal — `found` false ⇒ the surface could not place the
 *  cited text (orphaned, or the document is not painted yet). */
export interface RevealResult {
  readonly found: boolean;
  /** The surface's verdict, or `missing` when it could not place the quote. */
  readonly state: AnchorState | "missing";
  /** The anchor-owning (thread ROOT) comment id that was targeted. */
  readonly anchorCommentId: string;
}

export interface UseCitationDeepLinkOptions {
  /** The document being viewed (route `:id`). */
  docId: string;
  /** `location.search` — the source of `?comment=<id>`. */
  search: string;
  /** True once the document is painted AND its comments are anchored, so a
   *  scroll target can exist. The hook waits for this before revealing. */
  ready: boolean;
  /**
   * Reveal the cited anchor: scroll to it and bloom it. Returns the anchor's
   * state, or `null` when the quote can no longer be placed — in which case the
   * host still opens the thread but must not pretend the jump happened.
   *
   * Production passes `<ReadCanvas>`'s `focusAnchor` straight through. A
   * permalink LANDING is instant (`"restore"`), not animated: the reviewer
   * clicked a link elsewhere and is waiting for the text, not watching a
   * viewport travel.
   */
  reveal: (rootCommentId: number) => AnchorState | null;
  /** Called after a successful resolve. `revealed` is false when the cited span
   *  was not in the DOM (orphaned / not rendered) — open the thread anyway. */
  onResolved?: (resolution: CitationResolution, revealed: boolean) => void;
  /** Called when the resolve fails (404 no-access / unknown citation, network). */
  onError?: (error: unknown) => void;
}

export interface CitationDeepLinkState {
  /** The cited comment id from the URL, or null when there is no deep-link. */
  readonly targetCommentId: number | null;
  /** The last resolution, once fetched. */
  readonly resolution: CitationResolution | null;
  /** The last reveal outcome, once the canvas was ready. */
  readonly reveal: RevealResult | null;
}

/**
 * Resolve + reveal a `?comment=<id>` deep-link. Returns the target id (for a
 * host that wants to render its state) and the resolution once available.
 */
export function useCitationDeepLink(
  opts: UseCitationDeepLinkOptions,
): CitationDeepLinkState {
  const { docId, search, ready, reveal: revealAnchor, onResolved, onError } = opts;
  const targetCommentId = parseCitationParam(search);

  const [resolution, setResolution] = useState<CitationResolution | null>(null);
  const [reveal, setReveal] = useState<RevealResult | null>(null);
  // Fire-once guard, keyed by (doc, comment) so a navigation to a NEW citation
  // re-arms it but a re-render does not re-fire.
  const handledKey = useRef<string | null>(null);

  useEffect(() => {
    if (targetCommentId == null) return;
    if (!ready) return;
    const key = `${docId}:${targetCommentId}`;
    if (handledKey.current === key) return;
    handledKey.current = key;

    let cancelled = false;
    void (async () => {
      try {
        const res = await api.resolveCitation(docId, targetCommentId);
        if (cancelled) return;
        setResolution(res);
        // The anchor lives on the thread ROOT — a permalink to a REPLY still
        // scrolls to the root's cited text, then the host opens the reply.
        const state = revealAnchor(res.anchor.root_id);
        const outcome: RevealResult = {
          found: state !== null,
          state: state ?? "missing",
          anchorCommentId: String(res.anchor.root_id),
        };
        setReveal(outcome);
        onResolved?.(res, outcome.found);
      } catch (e) {
        if (cancelled) return;
        // Re-arm so a later retry (e.g. after login) can succeed.
        handledKey.current = null;
        onError?.(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [docId, targetCommentId, ready, revealAnchor, onResolved, onError]);

  return { targetCommentId, resolution, reveal };
}
